// Reine Entscheidungen des Config-/Passwort-Flows von useToolConfig —
// herausgelöst, damit sie ohne DOM testbar sind. Der Hook ruft sie an
// denselben Stellen auf; Reihenfolge und Seiteneffekte bleiben im Hook.

import { ApiError } from '@/lib/api';
import { deriveLanguageConfig } from '@/lib/deriveLanguageConfig';
import type { ToolConfig } from '@shared/types';

export type ConfigStatus =
  | { kind: 'loading' }
  | { kind: 'password-required' }
  | { kind: 'error'; message: string; status?: number }
  | { kind: 'ready'; config: ToolConfig };

/** Getipptes Passwort nur für das Projekt, für das es eingegeben wurde
 *  (SPA-Wechsel zu Projekt B schickte sonst A's Passwort — gezählter
 *  Fehlversuch, Integrations-Review 2026-09-24). */
export function passwordForTool(
  typed: { toolId: string | undefined; pw: string } | null,
  toolId: string | undefined,
): string | undefined {
  return typed && typed.toolId === toolId ? typed.pw : undefined;
}

/** Geladene Config → wirksame Config fürs Talent-Frontend. Multi-Lingual:
 *  virtuelle Standard-Config der Sprache (`sessionLang` = Code groß), ohne
 *  bekannte Sprache ein 404-Fehler. Standard: unverändert, keine Sprache. */
export function resolveLoadedConfig(
  config: ToolConfig,
  lang: string | undefined,
): { ok: true; effective: ToolConfig; sessionLang: string | undefined } | { ok: false; status: ConfigStatus } {
  if (config.projectType !== 'multilingual') return { ok: true, effective: config, sessionLang: undefined };
  const derived = lang ? deriveLanguageConfig(config, lang) : null;
  if (!derived) return { ok: false, status: { kind: 'error', message: 'Unknown or missing language', status: 404 } };
  return { ok: true, effective: derived, sessionLang: lang!.toUpperCase() };
}

/** Fehler beim Config-Load → Status. 401 heißt „Passwort nötig"; kam es auf
 *  einen Versuch MIT Passwort, war das Passwort falsch (`attemptFailed`). */
export function configLoadFailure(err: unknown, hadPassword: boolean): { status: ConfigStatus; attemptFailed: boolean } {
  if (err instanceof ApiError) {
    if (err.status === 401) return { status: { kind: 'password-required' }, attemptFailed: hadPassword };
    return { status: { kind: 'error', message: err.message, status: err.status }, attemptFailed: false };
  }
  const msg = err instanceof Error ? err.message : 'Unknown error';
  return { status: { kind: 'error', message: msg }, attemptFailed: false };
}

/** 401 einer Talent-Route mitten in der Session: nur eine laufende
 *  Arbeitsansicht fällt auf die Passwortabfrage zurück. */
export function statusAfterUnauthorized(prev: ConfigStatus): ConfigStatus {
  return prev.kind === 'ready' ? { kind: 'password-required' } : prev;
}
